/* eslint-disable react/prop-types */
import { useRef, useState } from "react";
import { ImagePlus, Loader2, X, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "../ui/sonner";
import api, { formatErr } from "../../lib/api";
import { uploadFile, uploadUrl } from "../../lib/uploads";

const btn = (on) => `px-2 py-1 rounded border text-[11px] ${on ? "border-cyan-400 bg-cyan-500/15 text-cyan-200" : "border-white/10 text-[#a0aabf] hover:border-cyan-400/40"}`;
const NO_TOC = ["image", "image_grid", "cover", "contents", "intro"];
const FIELDS = [
  ["title", "Customer / site name", "Leopard Creek Golf Estate"],
  ["subtitle", "Report title", "Water Treatment Plant Proposal"],
  ["prepared_by", "Prepared by", "Prepared by HidroVerse"],
  ["date", "Date", "24 October 2026"],
  ["tagline", "Tagline", "Taking Pure Water Everywhere"],
];

const Photo = ({ id, h, fit = "cover" }) => (
  <img src={uploadUrl(id)} alt="" style={{ width: "100%", height: h, objectFit: fit, display: "block" }} />
);

function CoverPhotos({ c }) {
  const imgs = (c.images || []).filter((i) => i.upload_id);
  if (!imgs.length) return null;
  if (imgs.length === 1) return <Photo id={imgs[0].upload_id} h={170} fit="contain" />;
  const rowOf = (items, h) => {
    const cols = items.length <= 3 ? items.length : Math.ceil(items.length / 2);
    return (
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))`, gap: 4 }}>
        {items.map((im) => <Photo key={im.id} id={im.upload_id} h={h} />)}
      </div>
    );
  };
  if (c.photo_layout === "feature") {
    return <div className="space-y-1"><Photo id={imgs[0].upload_id} h={120} />{rowOf(imgs.slice(1), 60)}</div>;
  }
  return rowOf(imgs, imgs.length > 3 ? 70 : 100);
}

/** Read view + edit preview — same blocks, same order as the printed cover. */
export function CoverView({ sec }) {
  const c = sec.cover || {};
  return (
    <div className="mx-auto bg-white text-[#111] rounded shadow flex flex-col justify-evenly text-center px-6 py-5 gap-3" style={{ maxWidth: 420, minHeight: 560, fontFamily: "Montserrat, sans-serif" }} data-testid={`workbench-cover-view-${sec.id}`}>
      {c.title && <div className="text-2xl leading-tight font-medium">{c.title}</div>}
      {c.logo_upload_id && <img src={uploadUrl(c.logo_upload_id)} alt="" style={{ height: 90, objectFit: "contain", width: "100%" }} />}
      {c.subtitle && <div className="text-lg leading-snug">{c.subtitle}</div>}
      <CoverPhotos c={c} />
      {(c.prepared_by || c.date) && (
        <div>
          {c.prepared_by && <div className="text-xs mb-1">{c.prepared_by}</div>}
          {c.date && <div className="text-sm">{c.date}</div>}
        </div>
      )}
      {c.tagline && <div className="text-[10px] italic text-[#333]">{c.tagline}</div>}
    </div>
  );
}

export function ContentsView({ sec, sections }) {
  const items = (sections || []).filter((s) => s.heading && !NO_TOC.includes(s.kind));
  return (
    <div data-testid={`workbench-contents-view-${sec.id}`}>
      <h2 className="text-sm font-semibold text-cyan-200 mb-1">{sec.heading || "Contents"}</h2>
      <ol className="space-y-0.5">
        {items.map((s) => (
          <li key={s.id} className="flex gap-2 text-[11px] text-[#cbd5e1]">
            <span className="flex-1 border-b border-dotted border-white/15">{s.heading}</span>
          </li>
        ))}
      </ol>
      <div className="text-[10px] text-[#647087] mt-1">Page numbers are filled in on the PDF.</div>
    </div>
  );
}

function PhotoSlots({ c, set, sid }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const imgs = c.images || [];
  const add = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const added = [];
      for (const f of Array.from(files).slice(0, 6 - imgs.length)) {
        const up = await uploadFile(f, { target_kind: "workbench_report", label: f.name });
        added.push({ id: `img-${Math.random().toString(36).slice(2, 10)}`, upload_id: up.id, caption: "" });
      }
      set({ images: [...imgs, ...added] });
    } catch { toast.error("Image upload failed."); } finally { setBusy(false); }
  };
  const move = (i, d) => {
    const n = [...imgs];
    const j = i + d;
    if (j < 0 || j >= n.length) return;
    [n[i], n[j]] = [n[j], n[i]];
    set({ images: n });
  };
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
      {imgs.map((im, i) => (
        <div key={im.id} className="relative" data-testid={`workbench-cover-photo-${im.id}`}>
          <img src={uploadUrl(im.upload_id)} alt="" className="w-full h-16 object-cover rounded" />
          <div className="absolute top-1 right-1 flex gap-0.5">
            <button type="button" onClick={() => move(i, -1)} className="bg-black/60 rounded p-0.5 text-white" data-testid={`workbench-cover-photo-left-${im.id}`}><ChevronLeft className="w-3 h-3" /></button>
            <button type="button" onClick={() => move(i, 1)} className="bg-black/60 rounded p-0.5 text-white" data-testid={`workbench-cover-photo-right-${im.id}`}><ChevronRight className="w-3 h-3" /></button>
            <button type="button" onClick={() => set({ images: imgs.filter((x) => x.id !== im.id) })} className="bg-black/60 rounded p-0.5 text-rose-300" data-testid={`workbench-cover-photo-remove-${im.id}`}><X className="w-3 h-3" /></button>
          </div>
        </div>
      ))}
      {imgs.length < 6 && (
        <button type="button" disabled={busy} onClick={() => ref.current?.click()} className="h-16 rounded border border-dashed border-cyan-400/40 text-cyan-300 flex flex-col items-center justify-center text-[10px] hover:bg-cyan-500/5" data-testid={`workbench-cover-photo-add-${sid}`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
          {busy ? "Uploading…" : "Add photo"}
        </button>
      )}
      <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid={`workbench-cover-photo-file-${sid}`} />
    </div>
  );
}

function LogoSlot({ c, set, sid }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const pick = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const up = await uploadFile(f, { target_kind: "workbench_report", label: f.name });
      set({ logo_upload_id: up.id });
    } catch { toast.error("Logo upload failed."); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2">
      {c.logo_upload_id ? (
        <div className="relative bg-white rounded p-1">
          <img src={uploadUrl(c.logo_upload_id)} alt="" className="h-14 w-20 object-contain" data-testid={`workbench-cover-logo-${sid}`} />
          <button type="button" onClick={() => set({ logo_upload_id: null })} className="absolute -top-1 -right-1 bg-black/70 rounded-full p-0.5 text-rose-300" data-testid={`workbench-cover-logo-remove-${sid}`}><X className="w-3 h-3" /></button>
        </div>
      ) : (
        <button type="button" disabled={busy} onClick={() => ref.current?.click()} className={btn(false)} data-testid={`workbench-cover-logo-add-${sid}`}>
          {busy ? "Uploading…" : "Add logo"}
        </button>
      )}
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} data-testid={`workbench-cover-logo-file-${sid}`} />
    </div>
  );
}

export function CoverEditor({ sec, onPatch, reportPath }) {
  const c = sec.cover || {};
  const set = (patch) => onPatch({ cover: { ...c, ...patch } });
  const label = "text-[10px] uppercase tracking-wider text-[#647087]";
  const useCustomerLogo = async () => {
    try {
      const { data } = await api.get(`${reportPath}/customer-logo`);
      const cust = data.customer;
      if (!cust) return toast.error("This report isn't linked to a customer.");
      if (!cust.logo_upload_id) return toast.error(`${cust.name} has no logo saved — add it on their customer page.`);
      set({ logo_upload_id: cust.logo_upload_id });
      toast.success(`Using ${cust.name}'s logo`);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3" data-testid={`workbench-cover-editor-${sec.id}`}>
      <div className="text-[11px] text-[#a0aabf]">Only what you fill in is printed. Leave a line empty to leave it off the cover.</div>
      {FIELDS.map(([k, lbl, ph]) => (
        <label key={k} className="block space-y-0.5">
          <span className={label}>{lbl}</span>
          <div className="flex gap-1">
            <input
              key={`${k}-${c[k] || ""}`}
              defaultValue={c[k] || ""}
              placeholder={ph}
              onBlur={(e) => e.target.value !== (c[k] || "") && set({ [k]: e.target.value })}
              className="flex-1 bg-black/25 rounded px-2 py-1 text-[12px] text-[#cbd5e1] outline-none focus:ring-1 focus:ring-cyan-400/30"
              data-testid={`workbench-cover-${k}-${sec.id}`}
            />
            {c[k] && <button type="button" onClick={() => set({ [k]: "" })} className="px-1.5 text-[#647087] hover:text-rose-300" data-testid={`workbench-cover-${k}-clear-${sec.id}`}><X className="w-3 h-3" /></button>}
          </div>
        </label>
      ))}
      <div className="space-y-1">
        <span className={label}>Logo</span>
        <div className="flex items-center gap-2 flex-wrap">
          <LogoSlot c={c} set={set} sid={sec.id} />
          {reportPath && <button type="button" onClick={useCustomerLogo} className={btn(false)} data-testid={`workbench-cover-use-customer-logo-${sec.id}`}>Use customer logo</button>}
        </div>
      </div>
      <div className="space-y-1"><span className={label}>Photos (up to 6)</span><PhotoSlots c={c} set={set} sid={sec.id} /></div>
      {(c.images || []).length > 1 && (
        <div className={`flex flex-wrap items-center gap-1.5 ${label}`}>
          Photo layout
          <button type="button" className={btn(c.photo_layout !== "feature")} onClick={() => set({ photo_layout: "row" })} data-testid={`workbench-cover-layout-row-${sec.id}`}>Side by side</button>
          <button type="button" className={btn(c.photo_layout === "feature")} onClick={() => set({ photo_layout: "feature" })} data-testid={`workbench-cover-layout-feature-${sec.id}`}>One big + row below</button>
        </div>
      )}
      <div className="text-[10px] text-[#647087]">Preview (as printed):</div>
      <div className="rounded bg-white/5 p-2"><CoverView sec={sec} /></div>
    </div>
  );
}
